import { useState } from 'react'

import type { DestinoDeSobrante, SaldoDeSobrante } from '@/lib/supabase/tipos-bd'

import { ElegirOrden } from './ElegirOrden'
import { cantidadLegible, leerCantidad, useSalidaDeSobrante } from './useSobrantes'
import estilos from './PaginaSobrantes.module.css'

type Props = { saldo: SaldoDeSobrante; nombre: string; onListo: () => void }

const DESTINOS: [DestinoDeSobrante, string][] = [
  ['otra_orden', 'A una orden de trabajo'],
  ['descarte', 'Se descarta'],
  ['otro', 'Otro motivo'],
]

/**
 * Sacar un material del inventario de sobrantes: a una orden de trabajo
 * (0314), o descartarlo. Para varios materiales a la misma orden está
 * «Asignar a una orden», arriba de la tabla.
 */
export function DarSalida({ saldo, nombre, onListo }: Props) {
  const salida = useSalidaDeSobrante()
  const [texto, setTexto] = useState('')
  const [destino, setDestino] = useState<DestinoDeSobrante>('otra_orden')
  const [orden, setOrden] = useState('')
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const cantidad = leerCantidad(texto)
  const pasada = cantidad !== null && cantidad > saldo.saldo
  const lista =
    cantidad !== null &&
    cantidad > 0 &&
    !pasada &&
    (destino === 'otra_orden' ? orden !== '' : motivo.trim().length >= 4)

  async function guardar() {
    if (!lista || cantidad === null) return
    setError(null)
    try {
      await salida.mutateAsync({
        idInventario: saldo.id_inventario,
        cantidad,
        destino,
        motivo: motivo.trim() || null,
        ordenTrabajoId: destino === 'otra_orden' ? orden : null,
      })
      onListo()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo dar la salida.')
    }
  }

  return (
    <div className={estilos.salida}>
      <p className={estilos.salidaTitulo}>
        Salida de {nombre} · hay {cantidadLegible(saldo.saldo)} {saldo.unidad ?? ''}
      </p>

      <div className={estilos.salidaCampos}>
        <label className={estilos.campoCorto}>
          <span>Cuánto sale</span>
          <input
            type="text"
            inputMode="decimal"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="0"
            aria-invalid={pasada}
          />
        </label>

        <div className={estilos.destinos} role="group" aria-label="A dónde va">
          {DESTINOS.map(([valor, etiqueta]) => (
            <button
              key={valor}
              type="button"
              aria-pressed={destino === valor}
              className={destino === valor ? estilos.destinoVivo : undefined}
              onClick={() => setDestino(valor)}
            >
              {etiqueta}
            </button>
          ))}
        </div>

        {destino === 'otra_orden' && <ElegirOrden valor={orden} onCambio={setOrden} />}

        <label className={estilos.campoLargo}>
          <span>{destino === 'otra_orden' ? 'Nota (opcional)' : 'Por qué sale'}</span>
          <input
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={destino === 'descarte' ? 'Se dañó, se mojó, está incompleto…' : ''}
          />
        </label>
      </div>

      {pasada && (
        <p className={estilos.error}>Solo hay {cantidadLegible(saldo.saldo)}: no puede salir más.</p>
      )}
      {error && <p className={estilos.error}>{error}</p>}

      <div className={estilos.salidaBotones}>
        <button type="button" className={estilos.botonTexto} onClick={onListo}>
          Cancelar
        </button>
        <button
          type="button"
          className={estilos.botonFuerte}
          disabled={!lista || salida.isPending}
          onClick={() => void guardar()}
        >
          {salida.isPending ? 'Guardando…' : destino === 'otra_orden' ? 'Asignar' : 'Dar salida'}
        </button>
      </div>
    </div>
  )
}
